/**
 * Centralized configuration constants for the HireHuddle application
 * This file consolidates environment checks, admin settings, and shared configuration
 * to eliminate duplication and make changes easier.
 */

// Environment Configuration
export const ENV = {
  isDevelopment: import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development',
  isProduction: import.meta.env.PROD && import.meta.env.VITE_APP_ENV !== 'development',
};

// Admin Configuration
export const ADMIN_CONFIG = {
  // Full admin access - can view and modify
  adminEmails: ['727smorris@gmail.com', 'michaeldrew_@outlook.com', 'barlowjoshua079@gmail.com', 'brad@hirehuddle.co'],
  // View-only admin access - can view but not modify
  viewOnlyAdminEmails: ['bradew@gmail.com'],
};

// Industry Definitions with Specialties (max 5 selectable per user)
export const INDUSTRIES_WITH_SUBCATEGORIES = {
  'Advertising': {
    subcategories: [
      'Digital Advertising',
      'Creative Services',
      'Media Buying',
      'Brand Strategy',
      'Social Media Marketing',
      'Content Marketing',
      'SEO/SEM',
      'Public Relations',
      'Market Research',
      'Copywriting'
    ]
  },
  'Aerospace & Defense': {
    subcategories: [
      'Aircraft Manufacturing',
      'Defense Systems',
      'Space Technology',
      'Military Equipment',
      'Aviation Services',
      'Cybersecurity',
      'Government Contracting',
      'Research & Development'
    ]
  },
  'Agriculture, Food & Beverage': {
    subcategories: [
      'Farming & Ranching',
      'Food Processing',
      'Beverage Production',
      'Agricultural Technology',
      'Food Distribution',
      'Sustainable Agriculture',
      'Food Science',
      'Quality Assurance'
    ]
  },
  'Arts, Media & Entertainment': {
    subcategories: [
      'Film & Television',
      'Music',
      'Publishing',
      'Journalism',
      'Graphic Design',
      'Photography',
      'Animation',
      'Performing Arts',
      'Streaming & Digital Media',
      'Content Creation'
    ]
  },
  'Automotive': {
    subcategories: [
      'Vehicle Manufacturing',
      'Auto Parts & Accessories',
      'Dealerships',
      'Electric Vehicles',
      'Automotive Technology',
      'Fleet Management',
      'Automotive Design',
      'Service & Repair'
    ]
  },
  'Beauty & Personal Care': {
    subcategories: [
      'Cosmetics',
      'Skincare',
      'Haircare',
      'Fragrance',
      'Spa & Wellness',
      'Personal Care Products',
      'Beauty Technology',
      'Salon Services'
    ]
  },
  'Consulting & Professional Services': {
    subcategories: [
      'Management Consulting',
      'Strategy Consulting',
      'IT Consulting',
      'Financial Advisory',
      'Operations Consulting',
      'Business Analysis',
      'Change Management',
      'Digital Transformation'
    ]
  },
  'Consumer Packaged Goods (CPG)': {
    subcategories: [
      'Food & Beverage',
      'Household Products',
      'Personal Care',
      'Pet Products',
      'Brand Management',
      'Product Development',
      'Supply Chain',
      'Retail Strategy'
    ]
  },
  'Education & Training': {
    subcategories: [
      'K-12 Education',
      'Higher Education',
      'EdTech',
      'Corporate Training',
      'Online Learning',
      'Curriculum Development',
      'Special Education',
      'Vocational Training'
    ]
  },
  'Electronics': {
    subcategories: [
      'Consumer Electronics',
      'Semiconductors',
      'Hardware Engineering',
      'IoT Devices',
      'Audio/Video Equipment',
      'Computer Hardware',
      'Electronic Components',
      'Manufacturing'
    ]
  },
  'Energy, Utilities & Sustainability': {
    subcategories: [
      'Renewable Energy',
      'Oil & Gas',
      'Electric Utilities',
      'Solar',
      'Wind',
      'Sustainability',
      'Energy Storage',
      'Environmental Services'
    ]
  },
  'Financial Services': {
    subcategories: [
      'Investment Banking',
      'Commercial Banking',
      'Wealth Management',
      'Accounting',
      'Private Equity',
      'Venture Capital',
      'Corporate Finance',
      'Tax Services'
    ]
  },
  'Fintech & Payments': {
    subcategories: [
      'Digital Payments',
      'Blockchain',
      'Cryptocurrency',
      'Mobile Banking',
      'Lending Platforms',
      'RegTech',
      'InsurTech',
      'Financial Software'
    ]
  },
  'Food Service': {
    subcategories: [
      'Restaurants',
      'Quick Service',
      'Catering',
      'Food Delivery',
      'Culinary Arts',
      'Food & Beverage Management',
      'Bar Management',
      'Food Safety'
    ]
  },
  'Gaming & eSports': {
    subcategories: [
      'Game Development',
      'Game Design',
      'eSports Management',
      'Mobile Gaming',
      'Console Gaming',
      'PC Gaming',
      'Game Publishing',
      'Streaming & Content'
    ]
  },
  'Government & Public Service': {
    subcategories: [
      'Federal Government',
      'State & Local Government',
      'Public Policy',
      'Public Administration',
      'Law Enforcement',
      'Military',
      'Emergency Services',
      'Regulatory Affairs'
    ]
  },
  'Healthcare & Life Sciences': {
    subcategories: [
      'Hospitals & Health Systems',
      'Medical Devices',
      'Health IT',
      'Clinical Research',
      'Nursing',
      'Telemedicine',
      'Mental Health',
      'Public Health'
    ]
  },
  'Health, Wellness & Fitness': {
    subcategories: [
      'Fitness & Gyms',
      'Nutrition & Diet',
      'Mental Wellness',
      'Sports Medicine',
      'Wellness Technology',
      'Alternative Medicine',
      'Health Coaching',
      'Corporate Wellness'
    ]
  },
  'Hospitality': {
    subcategories: [
      'Hotels & Resorts',
      'Event Planning',
      'Casino & Gaming',
      'Cruise Lines',
      'Theme Parks',
      'Vacation Rentals',
      'Concierge Services',
      'Guest Relations'
    ]
  },
  'Human Resources & Talent Services': {
    subcategories: [
      'Recruiting & Talent Acquisition',
      'HR Operations',
      'Compensation & Benefits',
      'Training & Development',
      'HR Technology',
      'Diversity & Inclusion',
      'Payroll',
      'Employee Relations'
    ]
  },
  'Insurance & Risk Management': {
    subcategories: [
      'Life Insurance',
      'Health Insurance',
      'Property & Casualty',
      'Reinsurance',
      'Risk Assessment',
      'Claims Management',
      'Actuarial Services',
      'Underwriting'
    ]
  },
  'Legal Services': {
    subcategories: [
      'Corporate Law',
      'Litigation',
      'Intellectual Property',
      'Employment Law',
      'Compliance',
      'Contract Law',
      'Paralegal Services',
      'Legal Technology'
    ]
  },
  'Manufacturing & Industrial': {
    subcategories: [
      'Production & Operations',
      'Quality Control',
      'Supply Chain',
      'Industrial Engineering',
      'Plant Management',
      'Lean Manufacturing',
      'Automation',
      'Safety & Compliance'
    ]
  },
  'Nonprofit & Social Impact': {
    subcategories: [
      'Fundraising & Development',
      'Program Management',
      'Community Outreach',
      'Grant Writing',
      'Advocacy',
      'Social Work',
      'Volunteer Coordination',
      'Impact Measurement'
    ]
  },
  'Pets, Animals & Veterinary Services': {
    subcategories: [
      'Veterinary Care',
      'Pet Products',
      'Animal Welfare',
      'Pet Services',
      'Animal Health',
      'Pet Food',
      'Pet Technology',
      'Grooming & Boarding'
    ]
  },
  'Pharmaceuticals & Biotechnology': {
    subcategories: [
      'Drug Development',
      'Clinical Trials',
      'Regulatory Affairs',
      'Biotech Research',
      'Pharmaceutical Sales',
      'Quality Assurance',
      'Manufacturing',
      'Medical Affairs'
    ]
  },
  'Real Estate & Property Management': {
    subcategories: [
      'Residential Sales',
      'Commercial Real Estate',
      'Property Management',
      'Real Estate Development',
      'Leasing',
      'Real Estate Investment',
      'Facilities Management',
      'Appraisal & Valuation'
    ]
  },
  'Retail & E-commerce': {
    subcategories: [
      'E-commerce',
      'Store Operations',
      'Merchandising',
      'Inventory Management',
      'Customer Experience',
      'Omnichannel Retail',
      'Buying & Procurement',
      'Loss Prevention'
    ]
  },
  'Scientific Research': {
    subcategories: [
      'Laboratory Research',
      'Data Analysis',
      'Clinical Research',
      'Environmental Science',
      'Materials Science',
      'Research & Development',
      'Academic Research',
      'Scientific Writing'
    ]
  },
  'Software & SaaS': {
    subcategories: [
      'Software Development',
      'Product Management',
      'Cloud Computing',
      'DevOps',
      'Data Engineering',
      'Mobile Development',
      'Web Development',
      'QA & Testing'
    ]
  },
  'Technology & IT Services': {
    subcategories: [
      'IT Support',
      'Cybersecurity',
      'Network Engineering',
      'Systems Administration',
      'Database Administration',
      'IT Consulting',
      'Technical Support',
      'Infrastructure'
    ]
  },
  'Telecommunications & Networking': {
    subcategories: [
      'Network Operations',
      'Wireless/Mobile',
      'Broadband/Internet',
      'Telecom Infrastructure',
      'VoIP',
      'Satellite Communications',
      '5G Technology',
      'Network Security'
    ]
  },
  'Transportation, Logistics & Warehousing': {
    subcategories: [
      'Trucking & Freight',
      'Warehouse Operations',
      'Supply Chain Management',
      'Last Mile Delivery',
      'Fleet Management',
      'Shipping & Maritime',
      'Rail',
      'Aviation'
    ]
  },
  'Travel & Tourism': {
    subcategories: [
      'Travel Agencies',
      'Tour Operations',
      'Airlines',
      'Destination Marketing',
      'Travel Technology',
      'Adventure Travel',
      'Business Travel',
      'Travel Planning'
    ]
  },
  'Other / General': {
    subcategories: []
  }
};

// Migration mapping from old industry names to new ones
export const INDUSTRY_MIGRATION_MAP = {
  // Direct matches or close matches
  'Technology': 'Software & SaaS',
  'Healthcare': 'Healthcare & Life Sciences',
  'Finance': 'Financial Services',
  'Education': 'Education & Training',
  'Trades & Construction': 'Manufacturing & Industrial',
  'Marketing': 'Advertising',
  'Sales': 'Consulting & Professional Services',
  'Arts, Media & Entertainment': 'Arts, Media & Entertainment',
  'Manufacturing & Industrial': 'Manufacturing & Industrial',
  'Hospitality & Food Service': 'Hospitality',
  'Legal': 'Legal Services',
  'Government & Public Service': 'Government & Public Service',
  'Energy & Utilities': 'Energy, Utilities & Sustainability',
  'Transportation & Logistics': 'Transportation, Logistics & Warehousing',
  'Retail & Consumer Goods': 'Retail & E-commerce',
  'Non-Profit & Social Impact': 'Nonprofit & Social Impact',
  'Agriculture & Food': 'Agriculture, Food & Beverage',
  'Human Resources': 'Human Resources & Talent Services',
  'Consulting & Professional Services': 'Consulting & Professional Services',
  'Real Estate': 'Real Estate & Property Management',
  'Other': 'Other / General'
};

// Helper function to migrate old industry values to new ones
export const migrateIndustry = (oldIndustry) => {
  if (!oldIndustry) return null;
  // Check if it's already a new industry
  if (INDUSTRIES_WITH_SUBCATEGORIES[oldIndustry]) {
    return oldIndustry;
  }
  // Try to map from old to new
  return INDUSTRY_MIGRATION_MAP[oldIndustry] || oldIndustry;
};

// Helper function to migrate an array of industries
export const migrateIndustries = (oldIndustries) => {
  if (!oldIndustries || !Array.isArray(oldIndustries)) return [];
  return oldIndustries.map(migrateIndustry).filter(Boolean);
};

// Maximum number of specialties/subcategories a user can select
export const MAX_SPECIALTIES = 5;

// Flat list of industries for backward compatibility
export const INDUSTRIES = Object.keys(INDUSTRIES_WITH_SUBCATEGORIES);

// Helper function to get subcategories for an industry
export const getSubcategoriesForIndustry = (industry) => {
  return INDUSTRIES_WITH_SUBCATEGORIES[industry]?.subcategories || [];
};

// Helper function to get all subcategories across selected industries
export const getAllSubcategoriesForIndustries = (industries) => {
  if (!industries || !Array.isArray(industries)) return [];
  const subcats = new Set();
  industries.forEach(industry => {
    const subs = INDUSTRIES_WITH_SUBCATEGORIES[industry]?.subcategories || [];
    subs.forEach(sub => subcats.add(sub));
  });
  return Array.from(subcats).sort();
};

// Industry options for dropdown selects (value/label pairs)
export const INDUSTRY_OPTIONS = [
  { value: 'advertising', label: 'Advertising' },
  { value: 'aerospace_defense', label: 'Aerospace & Defense' },
  { value: 'agriculture_food_beverage', label: 'Agriculture, Food & Beverage' },
  { value: 'arts_media_entertainment', label: 'Arts, Media & Entertainment' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'beauty_personal_care', label: 'Beauty & Personal Care' },
  { value: 'consulting_professional_services', label: 'Consulting & Professional Services' },
  { value: 'cpg', label: 'Consumer Packaged Goods (CPG)' },
  { value: 'education_training', label: 'Education & Training' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'energy_utilities_sustainability', label: 'Energy, Utilities & Sustainability' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'fintech_payments', label: 'Fintech & Payments' },
  { value: 'food_service', label: 'Food Service' },
  { value: 'gaming_esports', label: 'Gaming & eSports' },
  { value: 'government_public_service', label: 'Government & Public Service' },
  { value: 'healthcare_life_sciences', label: 'Healthcare & Life Sciences' },
  { value: 'health_wellness_fitness', label: 'Health, Wellness & Fitness' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'human_resources_talent', label: 'Human Resources & Talent Services' },
  { value: 'insurance_risk', label: 'Insurance & Risk Management' },
  { value: 'legal_services', label: 'Legal Services' },
  { value: 'manufacturing_industrial', label: 'Manufacturing & Industrial' },
  { value: 'nonprofit_social_impact', label: 'Nonprofit & Social Impact' },
  { value: 'pets_animals_veterinary', label: 'Pets, Animals & Veterinary Services' },
  { value: 'pharma_biotech', label: 'Pharmaceuticals & Biotechnology' },
  { value: 'real_estate_property', label: 'Real Estate & Property Management' },
  { value: 'retail_ecommerce', label: 'Retail & E-commerce' },
  { value: 'scientific_research', label: 'Scientific Research' },
  { value: 'software_saas', label: 'Software & SaaS' },
  { value: 'technology_it', label: 'Technology & IT Services' },
  { value: 'telecom_networking', label: 'Telecommunications & Networking' },
  { value: 'transportation_logistics', label: 'Transportation, Logistics & Warehousing' },
  { value: 'travel_tourism', label: 'Travel & Tourism' },
  { value: 'other', label: 'Other / General' }
];

// Template Mappings for BLS Data
export const TEMPLATE_TO_BLS_OCCUPATION_MAP = {
  'Software Engineer': '15-1252',
  'Product Manager': '11-3021',
  'Data Scientist': '15-2051',
  'UX Designer': '27-1021',
  'Marketing Manager': '11-2021',
  'Sales Representative': '41-4011',
  'Financial Analyst': '13-2051',
  'Human Resources Manager': '11-3121',
  'Project Manager': '11-9199',
  'Business Analyst': '13-1111',
  'DevOps Engineer': '15-1252',
  'Customer Success Manager': '11-9199',
  'Account Executive': '41-3099',
  'Content Writer': '27-3043',
  'Graphic Designer': '27-1024',
  'Backend Engineer': '15-1252',
  'Frontend Engineer': '15-1252',
  'Full Stack Developer': '15-1252',
  'Mobile Developer': '15-1252',
  'QA Engineer': '15-1253',
  'Security Engineer': '15-1212',
  'Network Engineer': '15-1241',
  'Database Administrator': '15-1242',
  'Systems Administrator': '15-1244',
  'Cloud Architect': '15-1199',
  'Data Engineer': '15-1252',
  'Machine Learning Engineer': '15-1252',
  'AI Researcher': '15-2051',
  'UI Designer': '27-1021',
  'Product Designer': '27-1021',
  'Brand Manager': '11-2021',
  'Digital Marketing Specialist': '11-2021',
  'SEO Specialist': '13-1161',
  'Content Strategist': '27-3043',
  'Social Media Manager': '11-2021',
  'Growth Manager': '11-2021',
  'Operations Manager': '11-1021',
  'Supply Chain Manager': '11-3071',
  'Logistics Coordinator': '13-1081',
  'Technical Writer': '27-3042',
  'Recruiter': '13-1071',
  'Training Specialist': '13-1151',
  'Compensation Analyst': '13-1141',
  'Accountant': '13-2011',
  'Tax Specialist': '13-2082',
  'Investment Analyst': '13-2051',
  'Risk Analyst': '13-2099',
  'Compliance Officer': '13-1041',
  'Legal Counsel': '23-1011',
  'Paralegal': '23-2011',
  'Executive Assistant': '43-6011',
  'Office Manager': '11-3012',
  'Customer Support Specialist': '43-4051',
  'Technical Support Engineer': '15-1232',
};

// File Upload Constraints
export const FILE_CONSTRAINTS = {
  maxImageSize: 5 * 1024 * 1024, // 5MB in bytes
  maxResumeSize: 10 * 1024 * 1024, // 10MB in bytes
  maxVideoSize: 50 * 1024 * 1024, // 50MB in bytes
  allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  allowedResumeTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png'],
  allowedVideoTypes: ['video/webm', 'video/mp4'],
};

// Video Recording Configuration
export const VIDEO_CONFIG = {
  maxDuration: 30, // seconds
  countdownSeconds: 3,
  resolution: { width: 1280, height: 720 },
  mimeType: 'video/webm',
  questions: [
    'What are you looking for in your next role?',
    'What problem do you enjoy solving most in your work?',
    'How would a teammate describe working with you?',
  ],
};

// Image Processing Configuration
export const IMAGE_CONFIG = {
  profileImageQuality: 0.95,
  profileImageFormat: 'image/jpeg',
  profileImageFileName: 'profile-image.jpg',
  cropShape: 'round',
  cropAspectRatio: 1, // 1:1 for circular profile images
  zoomMin: 1,
  zoomMax: 3,
  zoomStep: 0.1,
};

// Firebase Storage Configuration
export const STORAGE_CONFIG = {
  uploadTimeout: 5000, // 5 seconds
  profileImagesPath: 'profile_images',
};

// LocalStorage Keys
export const STORAGE_KEYS = {
  userProfile: 'userProfile',
  isAuthenticated: 'isAuthenticated',
  pendingReviews: 'pendingReviews',
  chatRooms: 'chatRooms',
  trackerData: 'trackerData',
  resumeData: 'resumeData',
  themePreference: 'theme',
};

// UI Configuration
export const UI_CONFIG = {
  toastDuration: 3000, // 3 seconds
  debounceDelay: 300, // 300ms
  animationDuration: 200, // 200ms
};

// Validation Rules
export const VALIDATION = {
  minPasswordLength: 8,
  maxBioLength: 1000,
  maxUsernameLength: 30,
  minUsernameLength: 3,
  emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  urlRegex: /^https?:\/\/.+/,
};

// US States for location dropdown
export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

// Work Location Types
export const WORK_LOCATION_TYPES = [
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'On-site' }
];

// Feature Flags
export const FEATURES = {
  interviewRequests: ENV.isDevelopment,
  interviewCalendar: ENV.isDevelopment,
  chatRooms: true,
  reviews: true,
  tracker: true,
  resumeBuilder: true,
};

// API Configuration
export const API_CONFIG = {
  requestTimeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
};

// Error Messages
export const ERROR_MESSAGES = {
  uploadFailed: 'Failed to upload file. Please try again.',
  networkError: 'Network error. Please check your connection.',
  authRequired: 'You must be logged in to perform this action.',
  invalidFile: 'Invalid file type or size.',
  genericError: 'Something went wrong. Please try again.',
  permissionDenied: 'You do not have permission to perform this action.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  profileUpdated: 'Profile updated successfully!',
  imageSaved: 'Image saved successfully!',
  dataSaved: 'Data saved successfully!',
};

// ============================================================================
// TEAM & ORGANIZATION PERMISSIONS (Free Registration Expansion)
// ============================================================================

// Account Types
export const ACCOUNT_TYPES = {
  INDIVIDUAL: 'individual',
  BUSINESS: 'business',
};

// Team Roles
export const TEAM_ROLES = {
  OWNER: 'owner',      // Legacy - maps to admin
  ADMIN: 'admin',      // Full access: billing, subscription, company info, team management
  INTERVIEWER: 'interviewer', // Can view/edit interviews, request interviews, move cards, add notes
  READ: 'read',        // Read-only access to notes, can be added to meetings
  MEMBER: 'member',    // Legacy - maps to interviewer
};

// Permission Types
export const TEAM_PERMISSIONS = {
  INVITE_USERS: 'invite_users',
  REMOVE_USERS: 'remove_users',
  REQUEST_INTERVIEWS: 'request_interviews',
  USE_CREDITS: 'use_credits',
  UPGRADE_PLAN: 'upgrade_plan',
  MANAGE_BILLING: 'manage_billing',
  VIEW_TEAM_ACTIVITY: 'view_team_activity',
  EDIT_COMPANY_INFO: 'edit_company_info',
  VIEW_CALENDAR: 'view_calendar',
  EDIT_NOTES: 'edit_notes',
  VIEW_NOTES: 'view_notes',
  MOVE_INTERVIEW_CARDS: 'move_interview_cards',
  ATTEND_MEETINGS: 'attend_meetings',
};

// Role-Permission Mapping
export const ROLE_PERMISSIONS = {
  owner: [
    TEAM_PERMISSIONS.INVITE_USERS,
    TEAM_PERMISSIONS.REMOVE_USERS,
    TEAM_PERMISSIONS.REQUEST_INTERVIEWS,
    TEAM_PERMISSIONS.USE_CREDITS,
    TEAM_PERMISSIONS.UPGRADE_PLAN,
    TEAM_PERMISSIONS.MANAGE_BILLING,
    TEAM_PERMISSIONS.VIEW_TEAM_ACTIVITY,
    TEAM_PERMISSIONS.EDIT_COMPANY_INFO,
    TEAM_PERMISSIONS.VIEW_CALENDAR,
    TEAM_PERMISSIONS.EDIT_NOTES,
    TEAM_PERMISSIONS.VIEW_NOTES,
    TEAM_PERMISSIONS.MOVE_INTERVIEW_CARDS,
    TEAM_PERMISSIONS.ATTEND_MEETINGS,
  ],
  admin: [
    TEAM_PERMISSIONS.INVITE_USERS,
    TEAM_PERMISSIONS.REMOVE_USERS,
    TEAM_PERMISSIONS.REQUEST_INTERVIEWS,
    TEAM_PERMISSIONS.USE_CREDITS,
    TEAM_PERMISSIONS.UPGRADE_PLAN,
    TEAM_PERMISSIONS.MANAGE_BILLING,
    TEAM_PERMISSIONS.VIEW_TEAM_ACTIVITY,
    TEAM_PERMISSIONS.EDIT_COMPANY_INFO,
    TEAM_PERMISSIONS.VIEW_CALENDAR,
    TEAM_PERMISSIONS.EDIT_NOTES,
    TEAM_PERMISSIONS.VIEW_NOTES,
    TEAM_PERMISSIONS.MOVE_INTERVIEW_CARDS,
    TEAM_PERMISSIONS.ATTEND_MEETINGS,
  ],
  interviewer: [
    TEAM_PERMISSIONS.REQUEST_INTERVIEWS,
    TEAM_PERMISSIONS.USE_CREDITS,
    TEAM_PERMISSIONS.VIEW_TEAM_ACTIVITY,
    TEAM_PERMISSIONS.VIEW_CALENDAR,
    TEAM_PERMISSIONS.EDIT_NOTES,
    TEAM_PERMISSIONS.VIEW_NOTES,
    TEAM_PERMISSIONS.MOVE_INTERVIEW_CARDS,
    TEAM_PERMISSIONS.ATTEND_MEETINGS,
  ],
  read: [
    TEAM_PERMISSIONS.VIEW_NOTES,
    TEAM_PERMISSIONS.ATTEND_MEETINGS,
  ],
  member: [
    TEAM_PERMISSIONS.REQUEST_INTERVIEWS,
    TEAM_PERMISSIONS.USE_CREDITS,
    TEAM_PERMISSIONS.VIEW_TEAM_ACTIVITY,
    TEAM_PERMISSIONS.VIEW_CALENDAR,
    TEAM_PERMISSIONS.EDIT_NOTES,
    TEAM_PERMISSIONS.VIEW_NOTES,
    TEAM_PERMISSIONS.MOVE_INTERVIEW_CARDS,
    TEAM_PERMISSIONS.ATTEND_MEETINGS,
  ],
};

// Free Plan Limits (Soft Launch)
export const FREE_PLAN_LIMITS = {
  CREDITS: 4,
  INDIVIDUAL_SEATS: 1,
  BUSINESS_SEATS: 2,
  MAX_PENDING_INVITES: 3,
  INVITE_EXPIRY_DAYS: 7,
};

// Seat Status
export const SEAT_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  REMOVED: 'removed',
};

/**
 * Check if a role has a specific permission
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) || false;
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

// ============================================================================
// Tech Stack Options (Software, Tools, Technologies)
// ============================================================================

export const TECH_STACK_OPTIONS = [
  // Programming Languages
  'JavaScript', 'TypeScript', 'Python', 'Java', 'C#', 'C++', 'Go', 'Rust', 'PHP', 'Ruby',
  'Swift', 'Kotlin', 'Scala', 'R', 'MATLAB', 'Perl', 'Objective-C', 'Dart', 'Elixir', 'Clojure',

  // Frontend Frameworks & Libraries
  'React', 'Angular', 'Vue.js', 'Svelte', 'Next.js', 'Nuxt.js', 'Ember.js', 'Backbone.js',
  'jQuery', 'Bootstrap', 'Tailwind CSS', 'Material-UI', 'Ant Design', 'Chakra UI',

  // Backend Frameworks
  'Node.js', 'Express.js', 'Django', 'Flask', 'FastAPI', 'Ruby on Rails', 'Spring Boot',
  'ASP.NET', 'Laravel', 'Symfony', 'NestJS', 'Koa', 'Fastify',

  // Databases
  'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Oracle', 'SQL Server', 'MariaDB', 'Cassandra',
  'DynamoDB', 'Firebase', 'SQLite', 'CouchDB', 'Neo4j', 'Elasticsearch',

  // Database Tools
  'MySQL Workbench', 'pgAdmin', 'DBeaver', 'MongoDB Compass', 'DataGrip',

  // Cloud Platforms
  'AWS', 'Azure', 'Google Cloud Platform', 'Heroku', 'DigitalOcean',
  'Linode', 'Vultr', 'Vercel', 'Netlify', 'Railway', 'Render',

  // DevOps & CI/CD
  'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'CircleCI', 'Travis CI',
  'TeamCity', 'Terraform', 'Ansible', 'Chef', 'Puppet', 'Vagrant',

  // Version Control
  'Git', 'GitHub', 'GitLab', 'Bitbucket', 'Perforce',

  // IDEs & Code Editors
  'Visual Studio Code', 'IntelliJ IDEA', 'PyCharm', 'WebStorm', 'Sublime Text', 'Atom',
  'Notepad++', 'Eclipse', 'NetBeans', 'Xcode', 'Android Studio', 'Vim', 'Emacs',

  // AI-Assisted Coding & Tools
  'GitHub Copilot', 'Claude', 'Claude Code', 'Cursor', 'Windsurf', 'v0', 'Replit',
  'Perplexity', 'Ollama', 'Hugging Face', 'LangChain', 'Google Gemini', 'Mistral AI',

  // Testing Tools
  'Jest', 'Mocha', 'Jasmine', 'Selenium', 'Cypress', 'Playwright', 'Puppeteer', 'JUnit',
  'pytest', 'Postman', 'Insomnia',

  // Project Management & Collaboration
  'Jira', 'Trello', 'Asana', 'ClickUp', 'Linear', 'Notion', 'Confluence', 'Airtable',

  // Communication Tools
  'Slack', 'Discord', 'Zoom', 'Google Meet',

  // Design Tools
  'Figma', 'Sketch', 'Adobe XD', 'Adobe Photoshop', 'Adobe Illustrator',
  'Canva', 'Blender', 'AutoCAD',

  // Build Tools
  'Webpack', 'Vite', 'Rollup', 'Gradle', 'Maven', 'npm', 'yarn', 'pnpm',

  // Monitoring & Analytics
  'Google Analytics', 'Datadog', 'New Relic', 'Splunk', 'Grafana', 'Prometheus',
  'Sentry', 'Mixpanel', 'Hotjar',

  // CRM & Business Tools
  'Salesforce', 'HubSpot', 'Zendesk', 'Intercom',

  // E-commerce Platforms
  'Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'PrestaShop',

  // CMS Platforms
  'WordPress', 'Drupal', 'Contentful', 'Strapi', 'Sanity', 'Ghost',

  // Marketing Tools
  'Mailchimp', 'SEMrush', 'Google Ads', 'Meta',

  // Accounting & Finance
  'QuickBooks', 'Xero', 'SAP',

  // File Storage & Collaboration
  'Google Drive', 'Dropbox', 'Box', 'iCloud',

  // Other Tools
  'Zapier', 'IFTTT', 'Make', 'n8n', 'cURL', 'GraphQL',
  'REST APIs', 'SOAP', 'gRPC', 'WebSockets', 'Apache Kafka', 'RabbitMQ',
  'NGINX', 'Apache', 'HAProxy', 'Varnish', 'Cloudflare'
];

// ============================================================================
// FAQ Default Data (for Help Center)
// ============================================================================

// Default FAQ categories and questions
// Used as fallback when Firebase is empty and for seeding initial data
export const DEFAULT_FAQ_DATA = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "User",
    order: 0,
    questions: [
      {
        id: "gs-1",
        question: "How do I create an account?",
        answer: "Click the 'Sign Up' button on the landing page. You can register with your email address or sign up using your Google account for faster access. After signing up, you'll be guided through a quick onboarding process to set up your profile."
      },
      {
        id: "gs-2",
        question: "How do I complete my profile?",
        answer: "Navigate to your Profile page by clicking 'Profile' in the sidebar. Click the 'Edit' button to update your information including your bio, skills, experience, education, and job preferences. A complete profile helps hiring managers find you and increases your chances of getting interview requests."
      },
      {
        id: "gs-3",
        question: "What should I include in my profile?",
        answer: "For the best results, include: a professional photo or avatar, a compelling bio that highlights your experience, your key skills and technologies, work experience with descriptions, education background, and your job search preferences (desired roles, salary expectations, work type preferences)."
      },
      {
        id: "gs-4",
        question: "How do I make my profile visible to hiring managers?",
        answer: "In your Profile settings, enable 'Allow hiring managers to contact me'. This makes your profile discoverable to recruiters and hiring managers looking for candidates. You can disable this at any time if you're not actively job searching."
      }
    ]
  },
  {
    id: "resume-builder",
    title: "Resume Builder",
    icon: "FileText",
    order: 1,
    questions: [
      {
        id: "rb-1",
        question: "How do I create a resume?",
        answer: "Go to 'Resume Builder' in the sidebar and click 'Create New Resume'. Choose from our professional templates, then fill in your information section by section. Your resume is automatically saved as you type."
      },
      {
        id: "rb-2",
        question: "Can I have multiple resumes?",
        answer: "Yes! You can create multiple resumes tailored for different job types or industries. Each resume can use a different template and contain different content. This is useful for applying to varied positions."
      },
      {
        id: "rb-3",
        question: "How do I download my resume as a PDF?",
        answer: "Open your resume in the Resume Builder, then click the 'Export PDF' button. Your resume will be downloaded as a high-quality PDF file that you can share with employers or upload to job applications."
      },
      {
        id: "rb-4",
        question: "Can I change the template after creating my resume?",
        answer: "Yes, you can switch templates at any time. Your content will be preserved and reformatted to fit the new template. Open your resume and select a different template from the template options."
      },
      {
        id: "rb-5",
        question: "How do I customize the colors on my resume?",
        answer: "In the Resume Builder, look for the color/accent options. You can choose from preset colors or select a custom accent color that matches your personal brand or the company you're applying to."
      }
    ]
  },
  {
    id: "job-tracker",
    title: "Job Tracker",
    icon: "ClipboardList",
    order: 2,
    questions: [
      {
        id: "jt-1",
        question: "What is the Job Tracker?",
        answer: "The Job Tracker helps you organize and monitor your job applications. You can add jobs you've applied to, track their status (Applied, Interviewing, Offer, Rejected), add notes, and never lose track of where you are in your job search."
      },
      {
        id: "jt-2",
        question: "How do I add a job to the tracker?",
        answer: "Click 'Tracker' in the sidebar, then click 'Add Job' or the '+' button. Fill in the company name, position, application date, and current status. You can also add notes and the job posting URL for reference."
      },
      {
        id: "jt-3",
        question: "Can I update the status of my applications?",
        answer: "Yes, click on any job in your tracker to open its details. You can update the status, add interview dates, include notes about conversations with recruiters, and track your progress through the hiring pipeline."
      }
    ]
  },
  {
    id: "company-reviews",
    title: "Company Reviews",
    icon: "Star",
    order: 3,
    questions: [
      {
        id: "cr-1",
        question: "How do I write a company review?",
        answer: "Go to 'Reviews' in the sidebar and search for the company you want to review. Click on the company and select 'Write a Review'. Share your honest experience about the interview process, work culture, or employment experience."
      },
      {
        id: "cr-2",
        question: "Are reviews anonymous?",
        answer: "Yes, your reviews are anonymous by default. Other users will not see your name or profile information associated with your review. This allows you to share honest feedback without concerns."
      },
      {
        id: "cr-3",
        question: "What should I include in my review?",
        answer: "Helpful reviews typically include: the interview process and timeline, questions asked during interviews, company culture observations, pros and cons of working there, and any advice for future candidates."
      }
    ]
  },
  {
    id: "community",
    title: "Community & Threads",
    icon: "MessageCircle",
    order: 4,
    questions: [
      {
        id: "cm-1",
        question: "What are Threads?",
        answer: "Threads are community discussions where job seekers can ask questions, share experiences, and support each other. It's a space to connect with others who are going through the job search journey."
      },
      {
        id: "cm-2",
        question: "How do I start a new thread?",
        answer: "Go to 'Threads' in the sidebar and click 'New Thread' or the '+' button. Give your thread a title and write your post. You can ask questions, share tips, or start a discussion on any job-search related topic."
      },
      {
        id: "cm-3",
        question: "What are the community guidelines?",
        answer: "Be empathetic, supportive, and respectful. We're all on this journey together. Avoid spam, self-promotion, or negative behavior. Help others when you can, and don't share confidential information about companies or individuals."
      }
    ]
  },
  {
    id: "account-settings",
    title: "Account & Settings",
    icon: "Settings",
    order: 5,
    questions: [
      {
        id: "as-1",
        question: "How do I change my password?",
        answer: "Go to 'Settings' in the sidebar. In the Security section, you can update your password. You'll need to enter your new password and confirm it. Use a strong password with a mix of letters, numbers, and symbols."
      },
      {
        id: "as-2",
        question: "How do I switch between light and dark mode?",
        answer: "In Settings, you'll find the Theme toggle. Click it to switch between light and dark mode. Your preference is saved and will persist across sessions."
      },
      {
        id: "as-3",
        question: "How do I delete my account?",
        answer: "Go to Settings and scroll to the 'Danger Zone' section. Click 'Delete Account' and follow the confirmation steps. Please note that account deletion is permanent and cannot be undone. All your data will be removed."
      },
      {
        id: "as-4",
        question: "How do I log out?",
        answer: "Click on 'Settings' in the sidebar, then click the 'Log Out' button. You'll be signed out and redirected to the landing page."
      }
    ]
  }
];

// Soft Skills Options
// ============================================================================

export const SOFT_SKILLS_OPTIONS = [
  // Communication
  'Active Listening',
  'Clear Communication',
  'Public Speaking',
  'Written Communication',
  'Presentation Skills',
  'Storytelling',
  'Negotiation',
  'Persuasion',

  // Leadership & Management
  'Leadership',
  'Team Management',
  'Mentoring',
  'Coaching',
  'Delegation',
  'Conflict Resolution',
  'Decision Making',
  'Strategic Thinking',

  // Collaboration & Teamwork
  'Collaboration',
  'Teamwork',
  'Cross-functional Collaboration',
  'Relationship Building',
  'Networking',
  'Empathy',
  'Emotional Intelligence',
  'Reading the Room (Social Awareness)',
  'Respect for Diverse Perspectives',

  // Problem-Solving & Critical Thinking
  'Problem-Solving',
  'Critical Thinking',
  'Analytical Thinking',
  'Creative Thinking',
  'Innovation',
  'Research Skills',
  'Attention to Detail',

  // Adaptability & Resilience
  'Adaptability',
  'Flexibility',
  'Resilience',
  'Growth Mindset',
  'Learning Agility',
  'Stress Management',
  'Change Management',
  'Handling Ambiguity',

  // Work Ethic & Productivity
  'Time Management',
  'Prioritization',
  'Organization',
  'Self-Motivation',
  'Accountability',
  'Reliability',
  'Work Ethic',
  'Initiative',
  'Proactivity',

  // Customer & Client Focus
  'Customer Service',
  'Client Relations',
  'Customer Empathy',
  'Service Excellence',

  // Other Professional Skills
  'Cultural Awareness',
  'Diversity & Inclusion',
  'Integrity',
  'Professionalism',
  'Positive Attitude',
  'Curiosity',
  'Open-Mindedness',
  'Patience'
];
